// --- NEW FILE: apps/website/src/components/admin/tasks/ChecklistEditor.tsx ---

import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Plus, Trash2 } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import clsx from 'clsx';

import { Button, IconButton, Checkbox, TextField } from '@dopomogai/ui';
import type { ChecklistItem } from '@dopomogai/types';

// --- Internal Sortable Item Row ---
interface ChecklistItemRowProps {
  item: ChecklistItem;
  onItemChange: (newItem: ChecklistItem) => void;
  onItemRemove: () => void;
}

const ChecklistItemRow: React.FC<ChecklistItemRowProps> = ({ item, onItemChange, onItemRemove }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : 'auto',
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={clsx(
        "flex items-center gap-2 p-2 rounded-lg bg-white",
        isDragging && "shadow-lg ring-2 ring-emerald-500"
      )}
    >
      <button {...attributes} {...listeners} className="cursor-grab p-1 text-slate-400 focus:outline-none focus-visible:ring-2 rounded">
        <GripVertical size={18} />
      </button>
      <Checkbox
        id={`check-${item.id}`}
        checked={item.is_checked}
        onCheckedChange={(checked) => onItemChange({ ...item, is_checked: !!checked })}
      />
      <TextField
        name={`text-${item.id}`}
        value={item.text}
        onChange={(e) => onItemChange({ ...item, text: e.target.value })}
        className="flex-grow"
        containerClassName="w-full"
        placeholder="New checklist item..."
      />
      <IconButton icon={<Trash2 size={16} />} variant="ghost" onClick={onItemRemove} />
    </li>
  );
};


// --- Main Exported Component ---
interface ChecklistEditorProps {
  items: ChecklistItem[];
  onItemsChange: (newItems: ChecklistItem[]) => void;
  disabled?: boolean;
}

/**
 * An interactive editor for managing a list of checklist items, with support
 * for adding, deleting, editing, checking, and reordering.
 */
export const ChecklistEditor: React.FC<ChecklistEditorProps> = ({
  items,
  onItemsChange,
  disabled = false,
}) => {
  const { t } = useTranslation('admin_tasks');
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleItemChange = (changedItem: ChecklistItem) => {
    const newItems = items.map(item => item.id === changedItem.id ? changedItem : item);
    onItemsChange(newItems);
  };

  const handleItemRemove = (itemId: string) => {
    const newItems = items.filter((item) => item.id !== itemId);
    onItemsChange(newItems);
  };
  
  const handleAddItem = () => {
    const newItem: ChecklistItem = {
      id: uuidv4(),
      text: '',
      is_checked: false,
    };
    onItemsChange([...items, newItem]);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex(item => item.id === active.id);
      const newIndex = items.findIndex(item => item.id === over.id);
      onItemsChange(arrayMove(items, oldIndex, newIndex));
    }
  };

  return (
    <div className={clsx(disabled && "opacity-70")}>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={items} strategy={verticalListSortingStrategy}>
                <ul className="space-y-2">
                    {items.map((item) => (
                        <ChecklistItemRow
                            key={item.id}
                            item={item}
                            onItemChange={handleItemChange}
                            onItemRemove={() => handleItemRemove(item.id)}
                        />
                    ))}
                </ul>
            </SortableContext>
        </DndContext>
        
        {!disabled && (
            <div className="mt-4">
                <Button
                    textKey="editor.checklist.addItemButton"
                    variant="secondary"
                    icon={<Plus size={16} />}
                    onClick={handleAddItem}
                />
            </div>
        )}
    </div>
  );
};